import {
  commonSelectors,
  profileActions,
  tClientLicence,
  tClientLicences,
  userSharedSelectors,
  reduxTypedHooks,
  useIsPending,
  IStudentLicense
} from 'common/modules/ReactShared';
import { useCallback, useEffect } from 'react';

export type tAssignmentTaskLicensesReturnType = {
  licensesCorrectionInfo: {
    isUpdateLicensePending: boolean;
    currentLicense: tClientLicence;
    licensesSubmitFor: tClientLicences;
    requireLicenseSelector: boolean;
    changeCurrentLicense: (
      id: number | NonNullable<tClientLicence>
    ) => Promise<IStudentLicense>;
  };
  canSubmit: boolean;
  canAcceptAssignmentTask: boolean;
};
export default function useAssignmentTaskLicenses(): tAssignmentTaskLicensesReturnType {
  const dispatch = reduxTypedHooks.useTypedDispatch();

  const licenses = reduxTypedHooks.useTypedSelector(
    commonSelectors.selectLessonAssignmentLicenses
  );
  const currentLicense = reduxTypedHooks.useTypedSelector(
    userSharedSelectors.selectCurrentLicence
  );

  const isUpdateLicensePending = useIsPending([
    profileActions.changeCurrentLicence.typePrefix
  ]);

  const isFetchingProfile = useIsPending([profileActions.getProfile.typePrefix]);

  const isCurrentLicenseValid = isCurrentLicenseValidForTheAssignmentSubmit(
    currentLicense,
    licenses
  );
  const canSubmit = isCurrentLicenseValid && !isUpdateLicensePending;
  const canAcceptAssignmentTask = !isFetchingProfile && isCurrentLicenseValid;
  const requireLicenseSelector = isCurrentLicenseValid && licenses.length > 1;

  const changeCurrentLicense = useCallback(
    async (license: number | NonNullable<tClientLicence>) => {
      if (!license) throw new Error('no license to be selected');
      const licenseId = typeof license === 'number' ? license : license.id;
      // TODO do abort for prev if called one more time
      return dispatch(profileActions.changeCurrentLicence(licenseId)).unwrap();
    },
    [dispatch]
  );

  useEffect(() => {
    if (licenses.length && !isCurrentLicenseValid) {
      const licenseToActivate = licenses[0];
      changeCurrentLicense(licenseToActivate.id);
    }
  }, [changeCurrentLicense, isCurrentLicenseValid, licenses]);

  const licensesCorrectionInfo = {
    requireLicenseSelector,
    isUpdateLicensePending,
    currentLicense: isCurrentLicenseValid ? currentLicense : null,
    licensesSubmitFor: licenses,
    changeCurrentLicense
  };

  return { licensesCorrectionInfo, canSubmit, canAcceptAssignmentTask };
}

function isCurrentLicenseValidForTheAssignmentSubmit(
  currentLicense: tClientLicence,
  licenses: tClientLicences
) {
  if (!currentLicense) return false;
  if (licenses.some((l) => currentLicense.id === l.id)) return true;
  return false;
}
